import { Button, useTranslation } from '@hbs-us/ui';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { ConfirmDialog } from '../../_money/ConfirmDialog.js';
import { useMoneyFormatters } from '../../_money/useMoneyFormatters.js';
import type { JobDto } from '../contractsMirror.js';
import { useCreateInvoiceForJobMutation, useJobBillableQuery } from '../useJobQuery.js';

/**
 * Raise a draft invoice for whatever is still outstanding on the job.
 *
 * ⚠ IT BILLS THE REMAINING BALANCE, NOT THE CONTRACT. Billing the whole contract was only ever
 * right on a job nobody had invoiced yet, and change orders ended that: approving one raises the
 * contract, so re-billing the contract would ask the customer for everything again rather than for
 * the extra work. Outstanding = contract − already invoiced, archived invoices excluded.
 *
 * The confirm shows all three figures rather than a bare total, because "why is this number what it
 * is" is the question somebody about to send a customer a bill actually has.
 *
 * A button rather than something that fires when a job is created: a contractor who bills in
 * progress draws would otherwise collect a draft on every job, each having consumed an invoice
 * number from the shared sequence. The number comes from the server's max-numeric-suffix generator,
 * never last-row-wins.
 */
export function CreateInvoiceForJob({ job }: { job: JobDto }) {
  const { t } = useTranslation();
  const fmt = useMoneyFormatters();
  const navigate = useNavigate();
  const createInvoice = useCreateInvoiceForJobMutation(job.id);
  const billable = useJobBillableQuery(job.id);
  const [confirming, setConfirming] = useState(false);

  const contractAmount = billable.data?.contractAmount ?? Number(job.contractAmount ?? 0);
  const alreadyInvoiced = billable.data?.alreadyInvoiced ?? 0;
  const remaining = billable.data?.remaining ?? contractAmount;

  // Nothing outstanding is not an error worth a dialog — the control simply is not offered. That
  // covers a job with no contract AND a job already billed in full, which look the same to somebody
  // deciding whether there is a bill to raise.
  if (!(remaining > 0)) return null;

  return (
    <>
      <Button type="button" variant="secondary" onClick={() => setConfirming(true)}>
        {t('jobs:createInvoice.action')}
      </Button>

      {createInvoice.isError ? (
        <p role="alert" className="text-destructive text-sm">
          {t('jobs:createInvoice.error')}
        </p>
      ) : null}

      <ConfirmDialog
        open={confirming}
        heading={t('jobs:createInvoice.confirmHeading')}
        body={t('jobs:createInvoice.confirmBody', {
          amount: fmt.formatCurrency(remaining),
          contract: fmt.formatCurrency(contractAmount),
          invoiced: fmt.formatCurrency(alreadyInvoiced),
        })}
        confirmLabel={t('jobs:createInvoice.confirmAction')}
        cancelLabel={t('jobs:createInvoice.confirmCancel')}
        confirmPending={createInvoice.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          createInvoice.mutate(undefined, {
            // Straight to the draft: the next thing anybody does with a new invoice is check it,
            // and leaving them on the job means hunting for the number that was just minted.
            onSuccess: (result) =>
              void navigate({
                to: '/invoices/$id',
                params: { id: String(result.invoiceId) },
              }),
          });
        }}
      />
    </>
  );
}
